/* eslint-disable no-magic-numbers */
import { Colors } from '@futrli/colors';
import { createStyles, makeStyles } from '@mui/styles';
import { Theme } from '@emotion/react';
import { alpha, Menu, MenuProps } from '@mui/material';
import clsx from 'clsx';
import { omit } from 'lodash';
import React, { forwardRef } from 'react';
import typography from '../../ui-styles/sass/typography/typography.scss';

export type UIMenuExtendedClassKey = 'uiMenuTitle';

export interface UIMenuOwnProps {
  /**
   * Extended custom classes for being override.
   */
  extendedClasses?: Partial<Record<UIMenuExtendedClassKey, string>>;
  /**
   * Adds a title on the menu.
   */
  title?: string | React.ReactNode;
}

export type UIMenuProps = UIMenuOwnProps & Omit<MenuProps, keyof UIMenuOwnProps>;

interface UIMenuMakeStylesProps {
  classes: UIMenuProps['extendedClasses'];
}

const getPalette = (_theme: Theme, _props?: UIMenuProps | UIMenuMakeStylesProps) => {
  return {
    boxShadow: alpha(Colors.tealGrey.lighten10, 0.2),
    border: Colors.tealGrey.lighten40
  };
};

const useStyles = makeStyles<Theme, UIMenuProps>(theme => {
  const palette = getPalette(theme);

  return createStyles({
    paper: {
      border: `1px solid ${palette.border}`,
      boxShadow: `0 8px 24px -16px ${palette.boxShadow}`,
      minWidth: '180px'
    },
    list: {
      paddingTop: 0,
      paddingBottom: 0
    }
  });
});

const useExtendedStyles = makeStyles<Theme, UIMenuMakeStylesProps>(theme => {
  const palette = getPalette(theme);

  return createStyles({
    uiMenuTitle: {
      borderBottom: `1px solid ${palette.border}`,
      padding: '8px 16px'
    }
  });
});

export const UIMenu = forwardRef<HTMLDivElement, UIMenuProps>((props, ref) => {
  const { children, extendedClasses, title, open, disableAutoFocusItem = true, ...rest } = props;

  const uiClasses = useStyles(props);
  const extendedStyleClasses = useExtendedStyles({ classes: extendedClasses });

  const customClasses = {
    ...extendedStyleClasses,
    uiMenuTitle: clsx(extendedStyleClasses.uiMenuTitle, typography.uiTypeLabelAlt)
  };

  const muiProps = omit(rest, ['extendedClasses', 'title']);

  return (
    <Menu ref={ref} {...muiProps} open={open} classes={uiClasses} disableAutoFocusItem={disableAutoFocusItem}>
      {title && <div className={customClasses.uiMenuTitle}>{title}</div>}
      {children}
    </Menu>
  );
});
